<template>
  <div id="app">
    <transition name="page" mode="out-in">
      <router-view />
    </transition>
  </div>
</template>

<script>
const APP_DATA_VERSION = '3.1-real-backend-260509'

export default {
  name: 'App',
  async created() {
    // 登录成功后需要立即刷新后端数据，首次打开并触发版本清理时也必须可用。
    this.$root.initBackendData = this.initBackendData.bind(this)

    // 版本检查：大版本切换时强制清理旧 localStorage，防止 mock/真实数据混杂
    const savedVersion = localStorage.getItem('tr_app_data_version')
    if (savedVersion !== APP_DATA_VERSION) {
      this.clearAllLegacyData()
      localStorage.setItem('tr_app_data_version', APP_DATA_VERSION)
      if (this.$router && this.$route && this.$route.path !== '/login') {
        this.$router.replace('/login')
      }
      return
    }

    // 若已登录，先验证 token 是否仍被后端认可
    if (this.$store.state.user && this.$store.state.user.token) {
      const isValid = await this.validateAuth()
      if (!isValid) {
        this.$store.commit('LOGOUT')
        this.$router.replace('/login')
        return
      }
      // 认证通过后才从后端加载数据
      await this.initBackendData()
    }
  },
  methods: {
    clearAllLegacyData() {
      // 批量清理所有可能与旧 mock 数据相关的 localStorage key
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key) continue
        if (
          key.startsWith('talent_retention_v3_') ||
          key.startsWith('tr_v3_') ||
          key === 'user' ||
          key === 'evaluations' ||
          key === 'currentEvaluation' ||
          key === 'currentAIReport' ||
          key === 'role'
        ) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k))
    },
    async validateAuth() {
      try {
        await this.$api.fetchEmployees()
        return true
      } catch (e) {
        // 任何认证失败或网络不通都视为无效，强制重新登录
        // 不允许使用本地缓存数据，防止假数据误导
        return false
      }
    },
    async initBackendData() {
      // 应用启动时清空所有业务数据，防止本地旧/mock数据干扰
      // 数据必须从后端 API 获取，失败时页面显示空状态而非假数据
      this.clearBusinessData()

      const role = (this.$store.state.user && this.$store.state.user.role) || ''
      const isManager = role === 'manager'
      const isSuperAdmin = role === 'super_admin'

      // 并行发起所有独立请求，避免串行阻塞首屏加载
      // 配置类接口（settings + scoring_rules）所有角色都需要，确保前后端阈值一致
      const results = await Promise.allSettled([
        this.$api.fetchTasks(),
        this.$api.fetchEmployees(),
        this.$api.fetchBehaviors(),
        this.$api.fetchBehaviorCategories(),
        this.$api.fetchSettings(),
        ...(isManager ? [
          this.$api.fetchMyEvaluations(),
          this.$api.fetchResigned()
        ] : [
          this.$api.fetchInstructions(),
          this.$api.fetchSurveys(),
          this.$api.fetchAdminEvaluations(),
          this.$api.fetchResigned(),
          this.$api.fetchDepartureFeedbacks(),
          ...(isSuperAdmin ? [this.$api.fetchAdmins()] : []),
          this.$api.fetchContents()
        ])
      ])

      let tasksRes, empRes, behRes, catRes, settingsRes
      let instrRes, surveyRes, evalRes, resignedRes, feedbackRes, adminRes, contentRes

      if (isManager) {
        [tasksRes, empRes, behRes, catRes, settingsRes, evalRes, resignedRes] = results
        instrRes = surveyRes = feedbackRes = adminRes = contentRes = { status: 'rejected', reason: { message: 'Manager 无权限' } }
      } else if (isSuperAdmin) {
        [tasksRes, empRes, behRes, catRes, settingsRes, instrRes, surveyRes, evalRes, resignedRes, feedbackRes, adminRes, contentRes] = results
      } else {
        [tasksRes, empRes, behRes, catRes, settingsRes, instrRes, surveyRes, evalRes, resignedRes, feedbackRes, contentRes] = results
        adminRes = { status: 'rejected', reason: { message: '普通管理员无管理员列表权限' } }
      }

      // 任务列表：有后续路由恢复逻辑，单独处理
      if (tasksRes.status === 'fulfilled' && tasksRes.value.data) {
        this.$store.commit('SET_TASKS_LIST', tasksRes.value.data)
        const path = this.$route && this.$route.path || ''
        const match = path.match(/\/admin\/tasks\/(\d+)/)
        if (match) {
          const taskId = parseInt(match[1], 10)
          await this.$store.dispatch('initCurrentTask', { taskId })
        } else {
          await this.$store.dispatch('initCurrentTask', {})
        }
      } else if (tasksRes.status === 'rejected') {
        this.$message.error('[App] 获取任务列表失败: ' + (tasksRes.reason && tasksRes.reason.message || '未知错误'))
      }

      // 员工列表
      if (empRes.status === 'fulfilled' && empRes.value.data) {
        this.$store.commit('SET_EMPLOYEES', empRes.value.data)
      } else if (empRes.status === 'rejected') {
        this.$message.error('[App] 获取员工列表失败: ' + (empRes.reason && empRes.reason.message || '未知错误'))
      }

      // 行为库
      if (behRes.status === 'fulfilled' && behRes.value.data) {
        this.$store.commit('SET_BEHAVIORS', behRes.value.data)
      } else if (behRes.status === 'rejected') {
        this.$message.error('[App] 获取行为库失败: ' + (behRes.reason && behRes.reason.message || '未知错误'))
      }

      // 行为分类
      if (catRes.status === 'fulfilled' && catRes.value.data) {
        this.$store.commit('SET_CATEGORIES', catRes.value.data)
      } else if (catRes.status === 'rejected') {
        this.$message.error('[App] 获取行为分类失败: ' + (catRes.reason && catRes.reason.message || '未知错误'))
      }

      // 系统配置（fetchSettings 返回 Setting 列表，需遍历找到对应 config_key 的记录）
      if (settingsRes.status === 'fulfilled' && settingsRes.value.data) {
        const rawList = settingsRes.value.data
        const defaultSettings = {
          thresholds: { lowMax: 4, mediumMax: 10 },
          labels: { low: '低风险', medium: '中风险', high: '高风险' },
          survey: { expireDays: 3, baseUrl: 'http://localhost:8080/#/survey/' },
          system: { name: 'AI骨干人才保留助手', version: 'v3.1' }
        }
        // 将所有 config_key='settings' 的配置按 task_id 写入 settingsMap
        // 确保前台切换任务时 LOAD_TASK_SETTINGS 能找到对应配置，避免回退到默认值
        const settingsItems = rawList.filter(s => s.config_key === 'settings')
        if (settingsItems.length > 0) {
          settingsItems.forEach(item => {
            const tid = item.task_id || '1'
            this.$store.commit('SET_SETTINGS_FOR_TASK', {
              taskId: tid,
              settings: item.config_value
            })
          })
        } else {
          this.$store.commit('SET_SETTINGS', { ...defaultSettings })
        }
        // 解析 config_key='scoring_rules' 的配置（评分规则说明文本）
        const rulesItem = rawList.find(s => s.config_key === 'scoring_rules')
        if (rulesItem && rulesItem.config_value) {
          this.$store.commit('SET_SCORING_RULES', rulesItem.config_value)
        }
      } else if (settingsRes.status === 'rejected') {
        this.$message.error('[App] 获取系统配置失败: ' + (settingsRes.reason && settingsRes.reason.message || '未知错误'))
      }

      // 评估说明
      if (instrRes.status === 'fulfilled' && instrRes.value.data) {
        this.$store.commit('SET_INSTRUCTIONS', instrRes.value.data)
      } else if (instrRes.status === 'rejected' && !isManager) {
        this.$message.error('[App] 获取评估说明失败: ' + (instrRes.reason && instrRes.reason.message || '未知错误'))
      }

      // 调研列表
      if (surveyRes.status === 'fulfilled' && surveyRes.value.data) {
        this.$store.commit('SET_SURVEYS', surveyRes.value.data)
      } else if (surveyRes.status === 'rejected' && !isManager) {
        this.$message.error('[App] 获取调研列表失败: ' + (surveyRes.reason && surveyRes.reason.message || '未知错误'))
      }

      // 评估记录
      if (evalRes.status === 'fulfilled' && evalRes.value.data) {
        this.$store.commit('SET_EVALUATIONS', evalRes.value.data)
      } else if (evalRes.status === 'rejected' && !isManager) {
        this.$message.error('[App] 获取评估记录失败: ' + (evalRes.reason && evalRes.reason.message || '未知错误'))
      }

      // 离职员工
      if (resignedRes.status === 'fulfilled' && resignedRes.value.data) {
        this.$store.commit('SET_RESIGNED', resignedRes.value.data)
      } else if (resignedRes.status === 'rejected' && !isManager) {
        this.$message.error('[App] 获取离职员工失败: ' + (resignedRes.reason && resignedRes.reason.message || '未知错误'))
      }

      // 离职反馈
      if (feedbackRes.status === 'fulfilled' && feedbackRes.value.data) {
        this.$store.commit('SET_FEEDBACKS', feedbackRes.value.data)
      } else if (feedbackRes.status === 'rejected' && !isManager) {
        this.$message.error('[App] 获取离职反馈失败: ' + (feedbackRes.reason && feedbackRes.reason.message || '未知错误'))
      }

      // 管理员列表
      if (adminRes.status === 'fulfilled' && adminRes.value.data) {
        this.$store.commit('SET_ADMINS', adminRes.value.data)
      } else if (adminRes.status === 'rejected' && isSuperAdmin) {
        this.$message.error('[App] 获取管理员列表失败: ' + (adminRes.reason && adminRes.reason.message || '未知错误'))
      }

      // 内容列表
      if (contentRes.status === 'fulfilled' && contentRes.value.data) {
        this.$store.commit('SET_CONTENTS', contentRes.value.data)
      } else if (contentRes.status === 'rejected' && !isManager) {
        this.$message.error('[App] 获取内容列表失败: ' + (contentRes.reason && contentRes.reason.message || '未知错误'))
      }
    },
    clearBusinessData() {
      // 清空所有业务数据，确保不从本地缓存读取旧/mock数据
      this.$store.commit('SET_EMPLOYEES', [])
      this.$store.commit('SET_RESIGNED', [])
      this.$store.commit('SET_BEHAVIORS', [])
      this.$store.commit('SET_CATEGORIES', [])
      this.$store.commit('SET_SETTINGS', {})
      this.$store.commit('SET_INSTRUCTIONS', [])
      this.$store.commit('SET_SCORING_RULES', { content: '' })
      this.$store.commit('SET_TASKS_LIST', [])
      this.$store.commit('SET_SURVEYS', [])
      this.$store.commit('SET_SURVEY_QUESTIONS', [])
      this.$store.commit('SET_FEEDBACKS', [])
      this.$store.commit('SET_ADMINS', [])
      this.$store.commit('SET_CONTENTS', [])
      this.$store.commit('SET_USERS', [])
      this.$store.commit('SET_EVALUATIONS', [])
      this.$store.commit('SET_CURRENT_EVALUATION', null)
      this.$store.commit('SET_CURRENT_AI_REPORT', null)
    }
  }
}
</script>

<style>
#app {
  background: var(--color-bg);
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans SC", sans-serif;
}
</style>
